import { useCallback, useEffect, useRef, useState } from 'react';
import ApiDatabase from 'server';
import {
  deleteFailureOf,
  editFailureOf,
  isCommentSendable,
  sendFailureOf,
} from './invoiceCommentText';

// The list's own key: closing the panel cancels this read and nothing else.
export const COMMENTS_ABORT_KEY = 'admin/invoices/:number/comments';
export const COMMENT_ADD_ABORT_KEY = 'admin/invoices/:number/comments|add';
export const commentDeleteAbortKeyOf = (id) =>
  `admin/invoices/:number/comments/${id}|delete`;
export const commentEditAbortKeyOf = (id) =>
  `admin/invoices/:number/comments/${id}|edit`;

const withoutKey = (table, key) => {
  if (!(key in table)) return table;
  const { [key]: removed, ...rest } = table;
  return rest;
};

/**
 * The internal comments of an invoice (JBWEB-1279), oldest first. Held by the
 * panel rather than by its tab: the tab's title counts them from the moment
 * the panel opens, and switching tabs neither reads them again nor loses the
 * text being written. `failure` is "notFound" (404) or "failed";
 * `sendFailure` is "invalid", "notFound" or "failed".
 *
 * A comment whose `canDelete` the api set is deleted in one click
 * (JBWEB-1389): `deleteFailures` holds, by id, "forbidden" or "failed".
 * `onCommentDeleted` runs once the api has deleted one, so that the panel
 * reads the history again.
 *
 * A comment whose `canEdit` the api set can be edited in its row, one at a
 * time: `editing` is `{ id, original, draft }`, `editFailure` "invalid",
 * "forbidden" or "failed".
 */
const useInvoiceComments = (number, onCommentDeleted) => {
  const [list, setList] = useState({ items: null, failure: null });
  const [reloadKey, setReloadKey] = useState(0);
  const [draft, setDraft] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [sendFailure, setSendFailure] = useState(null);
  // A ref, not the state: two clicks in the same tick would both see
  // `isSending` false and post twice.
  const sendingRef = useRef(false);
  const mountedRef = useRef(true);
  const [deletingIds, setDeletingIds] = useState([]);
  const [deleteFailures, setDeleteFailures] = useState({});
  // Same reason as `sendingRef`: a double click must not send two DELETE.
  const deletingRef = useRef(new Set());
  // The panel passes a new function on each render: read through a ref so
  // that `deleteComment` stays stable.
  const onCommentDeletedRef = useRef(onCommentDeleted);
  onCommentDeletedRef.current = onCommentDeleted;
  const [editing, setEditing] = useState(null);
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [editFailure, setEditFailure] = useState(null);
  // Same reason as `sendingRef`: a double click must not send two PATCH.
  const savingEditRef = useRef(false);

  useEffect(() => {
    if (!number) return undefined;
    let active = true;
    setList((previous) => ({ ...previous, failure: null }));
    ApiDatabase.getInvoiceComments(
      { number },
      (data) => {
        if (!active) return;
        setList({
          items: Array.isArray(data?.items) ? data.items : [],
          failure: null,
        });
      },
      (error) => {
        if (!active || error?.name === 'CanceledError') return;
        // Never the error object in the console: axios keeps the request, token included.
        setList({
          items: null,
          failure: error?.response?.status === 404 ? 'notFound' : 'failed',
        });
      },
      { abortKey: COMMENTS_ABORT_KEY },
    );
    return () => {
      active = false;
    };
  }, [number, reloadKey]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      // The read only: a post already sent may have been written, cancelling
      // it would not undo it.
      ApiDatabase.abortRequest(COMMENTS_ABORT_KEY);
    };
  }, []);

  const reload = useCallback(() => {
    setList({ items: null, failure: null });
    setReloadKey((key) => key + 1);
  }, []);

  const changeDraft = useCallback((value) => {
    setDraft(value);
    setSendFailure(null);
  }, []);

  const send = useCallback(() => {
    if (sendingRef.current || !isCommentSendable(draft)) return;
    sendingRef.current = true;
    setIsSending(true);
    setSendFailure(null);
    ApiDatabase.postInvoiceComment(
      { number, text: draft.trim() },
      (data) => {
        sendingRef.current = false;
        if (!mountedRef.current) return;
        setIsSending(false);
        setDraft('');
        if (data?.comment) {
          setList((previous) => ({
            ...previous,
            items: [...(previous.items ?? []), data.comment],
          }));
        } else {
          reload();
        }
      },
      (error) => {
        sendingRef.current = false;
        if (!mountedRef.current) return;
        setIsSending(false);
        // The draft stays: the admin can send it again or fix it.
        setSendFailure(sendFailureOf(error));
      },
      { abortKey: COMMENT_ADD_ABORT_KEY },
    );
  }, [draft, number, reload]);

  const removeFromList = useCallback(
    (id) =>
      setList((previous) => ({
        ...previous,
        items: (previous.items ?? []).filter((item) => item.id !== id),
      })),
    [],
  );

  const deleteComment = useCallback(
    (id) => {
      if (deletingRef.current.has(id)) return;
      deletingRef.current.add(id);
      setDeletingIds((previous) => [...previous, id]);
      setDeleteFailures((previous) => withoutKey(previous, id));
      const settle = () => {
        deletingRef.current.delete(id);
        if (!mountedRef.current) return false;
        setDeletingIds((previous) => previous.filter((item) => item !== id));
        return true;
      };
      ApiDatabase.deleteInvoiceComment(
        { number, id },
        () => {
          if (!settle()) return;
          removeFromList(id);
          onCommentDeletedRef.current?.();
        },
        (error) => {
          if (!settle()) return;
          const failure = deleteFailureOf(error);
          if (failure === 'alreadyDeleted') {
            // Deleted meanwhile elsewhere: the list read again shows what
            // else may have changed, without the loading screen.
            removeFromList(id);
            setReloadKey((key) => key + 1);
            return;
          }
          setDeleteFailures((previous) => ({ ...previous, [id]: failure }));
        },
        { abortKey: commentDeleteAbortKeyOf(id) },
      );
    },
    [number, removeFromList],
  );

  const startEdit = useCallback((comment) => {
    if (savingEditRef.current) return;
    setEditing({ id: comment.id, original: comment.text, draft: comment.text });
    setEditFailure(null);
  }, []);

  const cancelEdit = useCallback(() => {
    if (savingEditRef.current) return;
    setEditing(null);
    setEditFailure(null);
  }, []);

  const changeEditDraft = useCallback((value) => {
    setEditing((current) => (current ? { ...current, draft: value } : current));
    setEditFailure(null);
  }, []);

  const saveEdit = useCallback(() => {
    if (!editing || savingEditRef.current) return;
    const { id, original, draft } = editing;
    if (!isCommentSendable(draft) || draft.trim() === original) return;
    savingEditRef.current = true;
    setIsSavingEdit(true);
    setEditFailure(null);
    ApiDatabase.patchInvoiceComment(
      { number, id, text: draft.trim() },
      (data) => {
        savingEditRef.current = false;
        if (!mountedRef.current) return;
        setIsSavingEdit(false);
        setEditing(null);
        if (data?.comment) {
          setList((previous) => ({
            ...previous,
            items: (previous.items ?? []).map((item) =>
              item.id === id ? data.comment : item,
            ),
          }));
        } else {
          setReloadKey((key) => key + 1);
        }
      },
      (error) => {
        savingEditRef.current = false;
        if (!mountedRef.current) return;
        setIsSavingEdit(false);
        const failure = editFailureOf(error);
        if (failure === 'alreadyDeleted') {
          // Deleted meanwhile elsewhere: nothing left to edit.
          setEditing(null);
          removeFromList(id);
          setReloadKey((key) => key + 1);
          return;
        }
        // The draft stays: the admin can save it again or fix it.
        setEditFailure(failure);
      },
      { abortKey: commentEditAbortKeyOf(id) },
    );
  }, [editing, number, removeFromList]);

  return {
    items: list.items,
    failure: list.failure,
    count: list.items ? list.items.length : null,
    reload,
    draft,
    changeDraft,
    send,
    isSending,
    sendFailure,
    deleteComment,
    deletingIds,
    deleteFailures,
    editing,
    startEdit,
    cancelEdit,
    changeEditDraft,
    saveEdit,
    isSavingEdit,
    editFailure,
  };
};

export default useInvoiceComments;
